/**
 * Toast notification manager.
 *
 * Creates a fixed container (bottom-right) and manages toast lifecycle:
 * creation, auto-dismiss timers, exit animations, and cleanup.
 *
 * @typedef {'success' | 'error' | 'warning' | 'info'} ToastType
 *
 * @typedef {Object} ToastOptions
 * @property {string} message
 * @property {ToastType} [type]
 * @property {number} [duration]
 */

const VALID_TYPES = new Set(['success', 'error', 'warning', 'info']);

/** @type {Record<ToastType, number>} */
const DEFAULT_DURATIONS = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 0,
};

const MAX_VISIBLE = 5;

// Must match .toast--exiting CSS animation duration
const EXIT_ANIMATION_MS = 200;

/**
 * @param {HTMLElement} container
 * @returns {{ show(options: ToastOptions): void, destroy(): void }}
 */
export function createToastManager(container) {
  let destroyed = false;

  const el = document.createElement('div');
  el.className = 'toast-container';
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-relevant', 'additions');
  container.appendChild(el);

  /** @type {Map<HTMLElement, { autoId?: number, exitId?: number }>} */
  const timers = new Map();

  /**
   * @param {HTMLElement} toastEl
   */
  function removeToast(toastEl) {
    const entry = timers.get(toastEl);
    if (entry) {
      if (entry.autoId) clearTimeout(entry.autoId);
      if (entry.exitId) clearTimeout(entry.exitId);
      timers.delete(toastEl);
    }
    toastEl.remove();
  }

  /**
   * @param {HTMLElement} toastEl
   */
  function dismiss(toastEl) {
    if (destroyed) return;
    if (toastEl.classList.contains('toast--exiting')) return;

    const entry = timers.get(toastEl);
    if (entry?.autoId) {
      clearTimeout(entry.autoId);
      entry.autoId = undefined;
    }

    toastEl.classList.add('toast--exiting');

    toastEl.addEventListener('animationend', () => removeToast(toastEl), { once: true });

    // Unconditional fallback — handles jsdom + prefers-reduced-motion
    const exitId = /** @type {number} */ (/** @type {unknown} */ (setTimeout(() => removeToast(toastEl), EXIT_ANIMATION_MS)));
    if (entry) {
      entry.exitId = exitId;
    }
  }

  /**
   * @param {ToastOptions} options
   */
  function show(options) {
    if (destroyed) return;

    const type = /** @type {ToastType} */ (VALID_TYPES.has(/** @type {string} */ (options.type)) ? options.type : 'info');
    const duration = options.duration !== undefined ? options.duration : DEFAULT_DURATIONS[type];

    // Evict oldest if at max capacity
    const visible = el.querySelectorAll('.toast:not(.toast--exiting)');
    if (visible.length >= MAX_VISIBLE) {
      dismiss(/** @type {HTMLElement} */ (visible[0]));
    }

    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    const msg = document.createElement('span');
    msg.className = 'toast-message';
    msg.textContent = options.message;

    const closeBtn = document.createElement('button');
    closeBtn.className = 'toast-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '\u00D7';
    closeBtn.addEventListener('click', () => dismiss(toast));

    toast.appendChild(msg);
    toast.appendChild(closeBtn);
    el.appendChild(toast);

    /** @type {{ autoId?: number, exitId?: number }} */
    const entry = {};
    timers.set(toast, entry);

    if (duration > 0) {
      entry.autoId = /** @type {number} */ (/** @type {unknown} */ (setTimeout(() => {
        entry.autoId = undefined;
        dismiss(toast);
      }, duration)));
    }
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    for (const [, entry] of timers) {
      if (entry.autoId) clearTimeout(entry.autoId);
      if (entry.exitId) clearTimeout(entry.exitId);
    }
    timers.clear();
    el.remove();
  }

  return { show, destroy };
}
